import React, { useMemo, memo } from "react"
import { colors } from "@/components/colors"

import "./speedGraph.scss"

interface DataPoint {
  value: number
  timeIndex: number
}

interface SpeedGraphProps {
  data: DataPoint[]
  width?: number
  height?: number
  color?: string
  selectionStart?: number
  selectionEnd?: number
  selectionColor?: string
  maxTime?: number
}

const GraphLine = memo(function GraphLine({
  data,
  width,
  height,
  color,
  maxTime,
}: {
  data: DataPoint[]
  width: number
  height: number
  color: string
  maxTime?: number
}) {
  const points = useMemo(() => {
    if (!data || data.length === 0) {
      return ""
    }

    const values = data.map((d) => d.value)
    const minValue = values.reduce((min, val) => (val < min ? val : min), values[0])
    const maxValue = values.reduce((max, val) => (val > max ? val : max), values[0])
    const range = maxValue - minValue || 1

    const lastTimeIndex = data[data.length - 1]?.timeIndex || 0
    const effectiveMaxTime = maxTime || lastTimeIndex

    return data
      .map((point) => {
        const x = (point.timeIndex / effectiveMaxTime) * width
        const y = height - ((point.value - minValue) / range) * height

        return `${x},${y}`
      })
      .join(" ")
  }, [data, width, height, maxTime])

  if (!points) {
    return null
  }

  return (
    <>
      {[0, 25, 50, 75, 100].map((percent) => (
        <line
          key={percent}
          className="grid-line"
          x1="0"
          y1={height * (percent / 100)}
          x2={width}
          y2={height * (percent / 100)}
        />
      ))}

      <polygon
        className="graph-area"
        points={`0,${height} ${points} ${width},${height}`}
        fill="url(#speedGradient)"
      />

      <polyline className="graph-line" points={points} stroke={color} />
    </>
  )
})

export default function SpeedGraph({
  data = [],
  width = 100,
  height = 60,
  color = colors.blue["800"],
  selectionStart = 0,
  selectionEnd = 1,
  selectionColor = "rgba(33, 150, 243, 0.15)",
  maxTime,
}: SpeedGraphProps) {
  if (!data || data.length === 0) {
    return null
  }

  const selectionX = selectionStart * width
  const selectionWidth = (selectionEnd - selectionStart) * width

  return (
    <div className="speed-graph-container" style={{ height: `${height}px` }}>
      <svg
        className="speed-graph-svg"
        width="100%"
        height={height}
        preserveAspectRatio="none"
        viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <linearGradient id="speedGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0.05} />
          </linearGradient>
        </defs>

        <rect
          x={selectionX}
          y={0}
          width={selectionWidth}
          height={height}
          fill={selectionColor}
          className="selection-area"
        />

        <GraphLine data={data} width={width} height={height} color={color} maxTime={maxTime} />
      </svg>
    </div>
  )
}
